/**
 * weekly-period-selects
 *
 * @author    Richard J. Turner <richard.turner@mediatel.co.uk>
 * @copyright 2015 Mediatel Limited.
 */

"use strict";

import getSelectedOptions from "../common/get-selected-options";

export default (periodsUrl, options={}) => sb => {
    let periods,
        yearSelect,
        monthSelect,
        weeksSelect;

    // Define the id of year, month, week elements
    options.datePartIds = ('undefined' !== typeof options.datePartIds)
        ? options.datePartIds
        : ["period_year", "period_month", "period_week"];

    options.type = ('undefined' !== typeof options.type)
        ? options.type
        : null;

    const fetchPeriods = function fetchPeriods() {
        if (periods !== void 0) {
            return Promise.resolve(periods);
        }

        // The period can be a week or 4 weeks
        let nDaysAgo = 6;
        if ('undefined' !== typeof options.weeksInterval && parseInt(options.weeksInterval) === 4) {
            nDaysAgo = 27;
        }

        // Set the parameters for API request
        const data = {
            weekly_period: ('0' + nDaysAgo).slice(-2),
            type: options.type
        };

        return sb.postApi(periodsUrl, data).then(
            weeks => {
                periods = weeks.reduce((acc, week) => {
                    let [year, month, day] = week.name.split(/-/);

                    year = parseInt(year, 10);
                    month = parseInt(month, 10);
                    day = parseInt(day, 10);

                    acc[year] = acc[year] || {};
                    acc[year][month] = acc[year][month] || {};

                    //noinspection JSUnresolvedFunction
                    let format = new Intl.DateTimeFormat("en-GB", {
                        month: "short",
                        day: "numeric"
                    });

                    let end = new Date(year, month - 1, day);
                    let start = new Date(year, month - 1, day).setDate(
                        end.getDate() - nDaysAgo
                    );

                    let name = format.format(start) + " - " + format.format(end);

                    acc[year][month][day] = { name, id: week.id };

                    return acc;
                }, {});

                return periods;
            }
        );
    };

    const parseWeekId = function parseWeek(weekId) {
        return [
            weekId.substr(0, 4),
            parseInt(weekId.substr(4, 2), 10).toString(),
            weekId
        ];
    };

    const getLatestWeek = function (periods) {
        const lastYear = Math.max(... Object.keys(periods));
        const lastMonth = Math.max(... Object.keys(periods[lastYear]));
        const lastWeek = Math.max(... Object.keys(periods[lastYear][lastMonth]));

        return periods[lastYear][lastMonth][lastWeek];
    };

    const calculateMonthsBeforeEnd = function (periods, monthsBeforeEnd) {
        const latestWeek = getLatestWeek(periods);
        const latestWeekDate = new Date(
            parseInt(latestWeek.id.substr(0, 4), 10),
            parseInt(latestWeek.id.substr(4, 2), 10) - 1,
            parseInt(latestWeek.id.substr(10, 2), 10)
        );

        latestWeekDate.setMonth(latestWeekDate.getMonth() - monthsBeforeEnd);
        latestWeekDate.setDate(latestWeekDate.getDate() + (
            latestWeekDate.getUTCDay() ? 7 - latestWeekDate.getUTCDay() : 0)
        );

        let [year, month, day] = latestWeekDate.toISOString().slice(0, 10).split("-");

        if (periods[year] === undefined) {
            year = parseInt(latestWeek.id.substr(0, 4), 10);
        }

        if (periods[year][month] === undefined) {
            month = Math.min(... Object.keys(periods[year]));
        }

        if (periods[year][month][day] === undefined) {
            day = Math.min(... Object.keys(periods[year][month]));
        }

        return [
            year,
            parseInt(month, 10).toString(),
            year + ("0" + month).slice(-2) + latestWeek.id.substr(6, 4) + ("0" + day).slice(-2)
        ];
    };

    const populatePeriodsSelects = function populatePeriodsSelects(params) {
        return fetchPeriods().then(pds => {
            let selectedYear,
                selectedMonth,
                selectedWeek,
                foundMonth = false;

            if (typeof params !== "undefined") {
                if (typeof params.startFrom !== "undefined") {
                    [selectedYear, selectedMonth, selectedWeek] = parseWeekId(params.startFrom);
                } else if (typeof params.monthsBeforeEnd !== "undefined") {
                    [selectedYear, selectedMonth, selectedWeek] = calculateMonthsBeforeEnd(pds, params.monthsBeforeEnd);
                } else {
                    selectedYear = typeof params.year !== "undefined" ? params.year : selectedYear;
                    selectedMonth = typeof params.month !== "undefined" ? params.month : selectedMonth;
                }
            }

            /* If the user has selected a year that isn't this year, select Jan
             for the month, otherwise it will default to the latest month. */
            if (selectedYear && !selectedMonth) {
                if (parseInt(selectedYear, 10) !== (new Date()).getUTCFullYear()) {
                    selectedMonth = 1;
                }
            }

            // Years (in reverse order)
            sb.empty("#" + options.datePartIds[0])
                .append("#" + options.datePartIds[0], Object.keys(pds).reverse().map(
                    year => {
                        selectedYear = selectedYear || year;
                        let selected = year === selectedYear ? "selected" : "";
                        return `<option ${selected} value="${year}">${year}</option>`;
                    }
                ).join("\n"));

            // Months: reverse to select the most recent month
            const psdLen = Object.keys(pds[selectedYear]).length;
            const monthOptions = Object.keys(pds[selectedYear]).reverse().map(
                (month, i) => {
                    if (selectedMonth === void 0) {
                        selectedMonth = selectedMonth || month;
                    }

                    let selected = '';
                    if(selectedMonth === month) {
                        selected = "selected";
                        foundMonth = true;
                    }

                    const date = new Date(2015, month - 1, 1);
                    const fmt = new Intl.DateTimeFormat("en-GB", {month: "long"});

                    const monthName = fmt.format(date);

                    /**
                     * If is the last element and the month don't match, set the first
                     * (the months' array is reversed) month as selected
                    **/
                    if((psdLen === i+1) && !foundMonth) {
                        selectedMonth = month;
                        selected = "selected";
                    }

                    return `<option ${selected} value="${month}">${monthName}</option>`;
                }
            );
            // Reverse again so they're displayed chronologically
            sb.empty("#" + options.datePartIds[1]).append(
                "#" + options.datePartIds[1],
                monthOptions.reverse().join("\n")
            );

            // Weeks: reverse to select the most recent week
            const weekOptions = Object.keys(pds[selectedYear][selectedMonth]).reverse().map(
                day => {
                    let week = pds[selectedYear][selectedMonth][day];
                    if (selectedWeek === void 0) {
                        selectedWeek = week.id;
                    }

                    let selected = selectedWeek === week.id ? "selected" : "";
                    return `<option ${selected} value=${week.id}>${week.name}</option>`;
                }
            );
            // Reverse again for chronological order
            sb.empty("#" + options.datePartIds[2]).append("#" + options.datePartIds[2], weekOptions.reverse().join("\n"));

            onWeekChange(); // We've added periods selected

            jQuery(weeksSelect[0]).trigger('option_change');
        });
    };

    const onYearChange = function onYearChange() {
        populatePeriodsSelects({
            year: yearSelect[0].value
        });
    };

    const onMonthChange = function onMonthChange() {
        populatePeriodsSelects({
            year: yearSelect[0].value,
            month: monthSelect[0].value
        });
    };

    const onWeekChange = function onWeekChange() {
        sb.notify({
            type: "periodsSelectionChanged",
            data: getSelectedOptions(weeksSelect[0])[0].value
        });
    };

    return {
        init() {
            yearSelect = sb.find("#" + options.datePartIds[0]);
            monthSelect = sb.find("#" + options.datePartIds[1]);
            weeksSelect = sb.find("#" + options.datePartIds[2]);

            if('undefined' !== typeof options.startFromPeriod) {
                populatePeriodsSelects({
                    startFrom: options.startFromPeriod
                });
            } else if('undefined' !== typeof options.startMonthsFromEnd) {
                populatePeriodsSelects({
                    monthsBeforeEnd: options.startMonthsFromEnd
                });
            } else {
                populatePeriodsSelects();
            }

            sb.addEvent(yearSelect, "change", onYearChange);
            sb.addEvent(monthSelect, "change", onMonthChange);
            sb.addEvent(weeksSelect, "change", onWeekChange);
        },
        destroy() {
            sb.removeEvent(weeksSelect, "change", onWeekChange);
            sb.removeEvent(monthSelect, "change", onMonthChange);
            sb.removeEvent(yearSelect, "change", onYearChange);

            periods = null;

            yearSelect = null;
            monthSelect = null;
            weeksSelect = null;
        }
    }
}
